/**
 * Coupon validation utility for Kundli Generation feature
 * This utility provides reusable functions for coupon validation and management
 */

import { astrologyEndPoint } from '../Services/AllApi';

// Default coupon codes (can be overridden by environment variables)
const DEFAULT_COUPONS = [
  'abhinay08', 'madhubani08', 'madhubani108', 'panda21', 'pujapanda88', 'madhubanicoupon99', 'divinepanda77', 'blessingsmadhubani11', 'panda2024', 'spiritualmadhubani33', 'madhubaniVIP09'
];

/**
 * Get valid coupon codes from environment variables or use defaults
 * @returns {Array<string>} Array of valid coupon codes
 */
export const getValidCoupons = () => {
  try {
    // Try to get coupons from environment variable
    const envCoupons = process.env.REACT_APP_COUPONS;
    if (envCoupons && envCoupons.trim()) {
      return envCoupons.split(',').map(coupon => coupon.trim()).filter(coupon => coupon.length > 0);
    }
  } catch (error) {
    console.warn('Failed to read coupons from environment variables:', error);
  }
  
  // Fallback to default coupons
  return DEFAULT_COUPONS;
};

/**
 * Validate if a coupon code is valid
 * @param {string} couponCode - The coupon code to validate
 * @returns {boolean} True if coupon is valid, false otherwise
 */
export const isValidCoupon = (couponCode) => {
  if (!couponCode || typeof couponCode !== 'string') {
    return false;
  }
  
  const validCoupons = getValidCoupons();
  const normalizedCoupon = couponCode.trim().toLowerCase();
  
  return validCoupons.some(validCoupon => 
    validCoupon.toLowerCase() === normalizedCoupon
  );
};

/**
 * Get coupon validation message
 * @param {string} couponCode - The coupon code to validate
 * @returns {Object} Object with isValid boolean and message string
 */
export const validateCoupon = (couponCode) => {
  if (!couponCode || couponCode.trim() === '') {
    return {
      isValid: true, // Empty coupon is valid (optional field)
      message: '',
      type: 'empty'
    };
  }
  
  const isValid = isValidCoupon(couponCode);
  
  if (isValid) {
    return {
      isValid: true,
      message: 'Valid coupon code! You will get advanced kundli features.',
      type: 'valid'
    };
  } else {
    return {
      isValid: false,
      message: 'Try without coupon or contact any Pandit near by in Madhubani for a coupon.',
      type: 'invalid'
    };
  }
};

/**
 * Get the appropriate API endpoint based on coupon validation
 * @param {string} couponCode - The coupon code to check
 * @param {Object} baseParams - Base parameters for the API call
 * @returns {Object} Object with endpoint URL and parameters
 */
export const getKundliApiConfig = (couponCode, baseParams) => {
  const validation = validateCoupon(couponCode);
  
  if (validation.isValid && validation.type === 'valid') {
    // Use advanced endpoint for valid coupon
    return {
      endpoint: astrologyEndPoint.KUNDLI_ADVANCED_API,
      params: {
        ayanamsa: baseParams.ayanamsa || 1,
        coordinates: baseParams.coordinates,
        datetime: baseParams.datetime,
        la: baseParams.language || 'en'
      },
      isAdvanced: true
    };
  } else {
    // Use fallback endpoint for invalid or empty coupon
    return {
      endpoint: astrologyEndPoint.KUNDLI_API,
      params: {
        ayanamsa: baseParams.ayanamsa || 1,
        coordinates: baseParams.coordinates,
        datetime: baseParams.datetime,
        name: baseParams.name,
        phone: baseParams.phone,
        la: baseParams.language || 'en'
      },
      isAdvanced: false
    };
  }
};

/**
 * Format API parameters for URL construction
 * @param {Object} params - Parameters object
 * @returns {string} Formatted query string
 */
export const formatApiParams = (params) => {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
};

const couponUtils = {
  getValidCoupons,
  isValidCoupon,
  validateCoupon,
  getKundliApiConfig,
  formatApiParams
};

export default couponUtils;
